import {useEffect, useMemo, useState} from "react";
import {toast} from "sonner";
import NiceModal from "@ebay/nice-modal-react";
import {useGetPartnerCodes} from "../api/requests/partner-level/getPartnerCodes";
import SwitchPartnerDialog from "../ui/SwitchPartnerDialog";
import {PartnersGroupItem} from "../declarations";

interface UsePartnerSwitcherProps {
  username: string | undefined;
  partnerCode: string | null | undefined;
  session: any;
  partnersGroup: PartnersGroupItem[];
  setPartnerCode: (code: string) => void;
  setPartnersGroup: (partners: PartnersGroupItem[]) => void;
  isInternal: boolean;
  isMultiPartnerUser: boolean;
}

interface UsePartnerSwitcherReturn {
  switcherAnchorEl: HTMLElement | null;
  showSwitcherDropdown: boolean;
  handleSwitcherClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleSwitcherClose: () => void;
  handlePartnerSelect: (selectedPartnerCode: string) => void;
  currentPartnerSymbol: string | undefined;
}

export const usePartnerSwitcher = ({
  partnerCode,
  session,
  partnersGroup,
  setPartnerCode,
  setPartnersGroup,
  isInternal,
}: UsePartnerSwitcherProps): UsePartnerSwitcherReturn => {
  const [switcherAnchorEl, setSwitcherAnchorEl] = useState<HTMLElement | null>(null);
  const showSwitcherDropdown = Boolean(switcherAnchorEl);

  const shouldFetchPartnerCodes =
    isInternal && partnersGroup.length === 0 && !!partnerCode && !!session?.tokens?.accessToken;

  const {data: partnerCodes, error: partnerCodesError} = useGetPartnerCodes({
    enabled: shouldFetchPartnerCodes,
  });

  useEffect(() => {
    if (partnerCodes && partnerCodes.length > 0) {
      setPartnersGroup(partnerCodes);
    }
  }, [partnerCodes, setPartnersGroup]);

  useEffect(() => {
    if (partnerCodesError) {
      toast.error("Failed to load partner list. Please try refreshing the page.");
    }
  }, [partnerCodesError]);

  const currentPartnerSymbol = useMemo(() => {
    const currentPartner = partnersGroup.find((p) => p.code === partnerCode);
    return currentPartner?.symbol_url;
  }, [partnersGroup, partnerCode]);

  const handleSwitcherClick = (event: React.MouseEvent<HTMLElement>) => {
    setSwitcherAnchorEl(event.currentTarget);
  };

  const handleSwitcherClose = () => {
    setSwitcherAnchorEl(null);
  };

  const handlePartnerSelect = (selectedPartnerCode: string) => {
    const selectedPartner = partnersGroup.find((p) => p.code === selectedPartnerCode);
    if (!selectedPartner || selectedPartnerCode === partnerCode) {
      handleSwitcherClose();
      return;
    }

    NiceModal.show(SwitchPartnerDialog, {
      partnerName: selectedPartner.display_name || selectedPartner.name,
      onConfirm: () => {
        setPartnerCode(selectedPartnerCode);
        sessionStorage.setItem("partnerId", selectedPartnerCode);
        handleSwitcherClose();
        window.location.reload();
      },
    });
  };

  return {
    switcherAnchorEl,
    showSwitcherDropdown,
    handleSwitcherClick,
    handleSwitcherClose,
    handlePartnerSelect,
    currentPartnerSymbol,
  };
};
